import React from "react";
import "./EditorLeftMenuBackgroundControls.css";
import BackgroundControlColorMenu from "./BackgroundControlColorMenu";
import EditorLeftMenuImageBackgroundControls from "./EditorLeftMenuImageBackgroundControls";
import EditorLeftMenuAiBackgroundControls from "./EditorLeftMenuAiBackgroundControls";

const BG_MENUS = {
  COLOR: "Color",
  IMAGE: "Image",
  AI: "AI",
  TOOLS: "Tools",
};

const EditorLeftMenuBackgroundControls = ({
  onApplyBackground = () => {},
  onRemoveBackground = () => {},
  onApplyImageBackground = () => {},
  onApplyAiBackground = () => {},
  onRemoveImageBackground = () => {},
  onRemoveAiBackground = () => {},
  backgroundState = {
    type: null,
    color: "transparent",
    imageUrl: null,
    aiConfig: null,
  },
  subjectPreviewUrl = null,
  onAiBackgroundPromptGenerate,
  aiBackgroundGenerating = false,
  aiBackgroundError = null,
  onUnsplashSearch,
  onUnsplashTrackDownload,
}) => {
  const [activeMenu, setActiveMenu] = React.useState(BG_MENUS.COLOR);
  const [viewingCategory, setViewingCategory] = React.useState(false);
  const showAiBackground = false;

  React.useEffect(() => {
    let menuToActivate = null;

    if (backgroundState?.type === "color") {
      menuToActivate = BG_MENUS.COLOR;
    } else if (backgroundState?.type === "image") {
      menuToActivate = BG_MENUS.IMAGE;
    } else if (backgroundState?.type === "ai") {
      menuToActivate = showAiBackground ? BG_MENUS.AI : BG_MENUS.COLOR;
    } else {
      return;
    }

    setActiveMenu((prev) => (prev === menuToActivate ? prev : menuToActivate));
  }, [backgroundState?.type, showAiBackground]);

  const handleApplyImageBackground = (imageUrl, attribution = null) => {
    onApplyImageBackground(imageUrl, attribution);
  };

  const handleCategoryViewChange = (isViewing) => {
    setViewingCategory(isViewing);
  };

  const handleMenuChange = (menu) => {
    setActiveMenu(menu);
  };

  const handleRemove = () => {
    if (activeMenu === BG_MENUS.COLOR) onRemoveBackground();
    else if (activeMenu === BG_MENUS.IMAGE) onRemoveImageBackground();
    else if (activeMenu === BG_MENUS.AI) onRemoveAiBackground();
  };

  const removeLabel =
    activeMenu === BG_MENUS.COLOR
      ? "Remove Background Color"
      : activeMenu === BG_MENUS.IMAGE
        ? "Remove Background Image"
        : "Remove AI Background";

  const showRemove =
    activeMenu === BG_MENUS.COLOR ||
    activeMenu === BG_MENUS.IMAGE ||
    activeMenu === BG_MENUS.AI;

  return (
    <div className="background-controls-container">
      {!viewingCategory && (
        <div className="background-controls-container__header">
          <h1>Background</h1>

          <div
            className="bg-sub-tabs"
            role="tablist"
            aria-label="Background options"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeMenu === BG_MENUS.COLOR}
              className={`bg-sub-tabs__btn${
                activeMenu === BG_MENUS.COLOR ? " bg-sub-tabs__btn--active" : ""
              }`}
              onClick={() => handleMenuChange(BG_MENUS.COLOR)}
            >
              Colors
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeMenu === BG_MENUS.IMAGE}
              className={`bg-sub-tabs__btn${
                activeMenu === BG_MENUS.IMAGE ? " bg-sub-tabs__btn--active" : ""
              }`}
              onClick={() => handleMenuChange(BG_MENUS.IMAGE)}
            >
              Images
            </button>
            {showAiBackground ? (
              <button
                type="button"
                role="tab"
                aria-selected={activeMenu === BG_MENUS.AI}
                className={`bg-sub-tabs__btn${
                  activeMenu === BG_MENUS.AI ? " bg-sub-tabs__btn--active" : ""
                }`}
                onClick={() => handleMenuChange(BG_MENUS.AI)}
                title="AI Backgrounds"
              >
                Ai
              </button>
            ) : null}
            <button
              type="button"
              role="tab"
              aria-selected={activeMenu === BG_MENUS.TOOLS}
              className={`bg-sub-tabs__btn${
                activeMenu === BG_MENUS.TOOLS ? " bg-sub-tabs__btn--active" : ""
              }`}
              onClick={() => handleMenuChange(BG_MENUS.TOOLS)}
            >
              Tools
            </button>
          </div>

          {showRemove ? (
            <div className="bg-remove-row">
              <button
                type="button"
                className="bg-remove-action-btn"
                onClick={handleRemove}
              >
                <span>🗑️</span> {removeLabel}
              </button>
            </div>
          ) : null}
        </div>
      )}

      <div className="background-controls-container__body">
        {activeMenu === BG_MENUS.TOOLS ? (
          <div className="bg-tools-placeholder">
            <p className="bg-tools-placeholder__title">Background tools</p>
            <p className="bg-tools-placeholder__text">
              Blur and other background tools will show up here soon.
            </p>
          </div>
        ) : null}

        {activeMenu === BG_MENUS.COLOR ? (
          <BackgroundControlColorMenu
            onApplyBackground={onApplyBackground}
            currentBgColor={backgroundState?.color || "transparent"}
          />
        ) : null}

        {activeMenu === BG_MENUS.IMAGE ? (
          <EditorLeftMenuImageBackgroundControls
            onApplyImageBackground={handleApplyImageBackground}
            onViewingCategoryChange={setViewingCategory}
            onUnsplashSearch={onUnsplashSearch}
            onUnsplashTrackDownload={onUnsplashTrackDownload}
          />
        ) : null}

        {showAiBackground && activeMenu === BG_MENUS.AI ? (
          <EditorLeftMenuAiBackgroundControls
            subjectPreviewUrl={subjectPreviewUrl}
            onApplyAiBackground={onApplyAiBackground}
            onCategoryViewChange={handleCategoryViewChange}
            onPromptGenerate={onAiBackgroundPromptGenerate}
            isGenerating={aiBackgroundGenerating}
            generationError={aiBackgroundError}
          />
        ) : null}
      </div>
    </div>
  );
};

export default EditorLeftMenuBackgroundControls;
